import { useMemo } from 'react';
import { Alert, Box, Checkbox, Chip, Sheet, Stack, Typography } from '@mui/joy';

import { useAdsPlacements } from './useAdsData';
import { FORMAT_LABELS, PRICING_LABELS, formatMoney } from './adsConstants';


/**
 * Выбор площадок размещения.
 *
 * Показываем только те, что поддерживают выбранную модель оплаты:
 * сервер всё равно отклонит несовместимую пару при отправке на модерацию,
 * и лучше не давать выбрать заведомо неработающее.
 */
export default function AdPlacementsPicker({
    pricingModel, selected, onChange, disabled,
}) {
    const { placements, loading } = useAdsPlacements();

    const available = useMemo(
        () => placements.filter(
            (p) => (p.allowed_pricing || ['cpc', 'cpm']).includes(pricingModel)
        ),
        [placements, pricingModel],
    );

    const minBid = useMemo(() => {
        const field = pricingModel === 'cpc' ? 'min_cpc' : 'min_cpm';
        const chosen = placements.filter((p) => selected.includes(p.code));
        if (!chosen.length) return 0;
        return Math.max(...chosen.map((p) => Number(p[field] || 0)));
    }, [placements, selected, pricingModel]);

    const toggle = (code) => {
        if (disabled) return;
        onChange(selected.includes(code)
            ? selected.filter((c) => c !== code)
            : [...selected, code]);
    };

    if (loading) {
        return <Typography level="body-sm" color="neutral">Загрузка площадок…</Typography>;
    }

    if (!available.length) {
        return (
            <Alert color="warning">
                Для модели «{PRICING_LABELS[pricingModel]}» доступных площадок нет.
                Смените модель оплаты.
            </Alert>
        );
    }

    return (
        <Box>
            <Stack spacing={1}>
                {available.map((placement) => {
                    const isSelected = selected.includes(placement.code);
                    const spec = placement.media_spec || {};
                    const image = spec.image || {};
                    const video = spec.video || {};

                    return (
                        <Sheet
                            key={placement.code}
                            variant={isSelected ? 'soft' : 'outlined'}
                            color={isSelected ? 'primary' : 'neutral'}
                            sx={{
                                p: 1.5, borderRadius: 'md',
                                cursor: disabled ? 'default' : 'pointer',
                            }}
                            onClick={() => toggle(placement.code)}
                        >
                            <Stack direction="row" spacing={1.2} alignItems="flex-start">
                                <Checkbox
                                    checked={isSelected}
                                    disabled={disabled}
                                    onChange={() => toggle(placement.code)}
                                    slotProps={{ input: { onClick: (e) => e.stopPropagation() } }}
                                    sx={{ mt: 0.3 }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                                        <Typography level="title-sm">{placement.name}</Typography>
                                        <Chip size="sm" variant="outlined">
                                            {FORMAT_LABELS[placement.format] || placement.format}
                                        </Chip>
                                        <Chip size="sm" variant="soft" color="primary">
                                            от {formatMoney(
                                                pricingModel === 'cpc' ? placement.min_cpc : placement.min_cpm
                                            )} ₽
                                        </Chip>
                                    </Stack>

                                    {placement.description && (
                                        <Typography level="body-sm" color="neutral" sx={{ mt: 0.4 }}>
                                            {placement.description}
                                        </Typography>
                                    )}

                                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.4 }}>
                                        {placement.screen_hint}
                                        {image.aspect ? ` · изображение ${image.aspect}` : ''}
                                        {image.min_width ? `, от ${image.min_width} px` : ''}
                                        {video.max_duration_sec ? ` · видео до ${video.max_duration_sec} сек` : ''}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Sheet>
                    );
                })}
            </Stack>

            {selected.length > 0 && (
                <Alert color="neutral" variant="soft" size="sm" sx={{ mt: 1.5 }}>
                    Минимальная ставка для выбранных площадок —{' '}
                    <b>{formatMoney(minBid)} ₽</b>{' '}
                    {pricingModel === 'cpc' ? 'за клик' : 'за 1000 показов'}.
                    Под каждый формат площадки нужно своё объявление.
                </Alert>
            )}
        </Box>
    );
}